import type { CarSpec } from 'noddi-types';
import { getCarDisplayName } from 'noddi-util';
import { isProduction, useAuthContext, useBookingCars, useLanguageContext } from 'noddi-web';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCurrentBrand } from '../stores/BrandStore/brandStore';
import { useServiceCenter, useStationaryBookingCar } from '../stores/StationaryBookingStore';
import { bookingSlugFromPathname } from './bookingSlugFromPathname';
import { toWidgetLocale } from './locale';
import { isNoddiSupportWidgetEnabled, syncNoddiSupportWidget } from './noddiSupportWidget';

const carLabel = (car: CarSpec) => getCarDisplayName({ car, showLicensePlate: false, showYear: false }) || undefined;

const plateNumber = (car: CarSpec) => car.licensePlate?.number || undefined;

export const NoddiSupportWidgetBridge = () => {
  const { currentBrand } = useCurrentBrand();
  const { currentLanguage } = useLanguageContext();
  const { userData, isLoggedIn } = useAuthContext();
  const { pathname } = useLocation();
  const mobileCars = useBookingCars();
  const stationaryCar = useStationaryBookingCar();
  const serviceCenter = useServiceCenter();

  const enabled = isNoddiSupportWidgetEnabled(currentBrand);
  const selectedCar = stationaryCar ?? mobileCars[0];
  const user = userData?.user;
  const userId = user?.id;
  const userEmail = user?.email;
  const userPhone = user?.phoneNumber;
  const userFirstName = user?.firstName;
  const userLastName = user?.lastName;
  const licensePlate = selectedCar ? plateNumber(selectedCar) : undefined;
  const car = selectedCar ? carLabel(selectedCar) : undefined;
  const serviceDepartmentId = serviceCenter?.serviceDepartmentId;
  const bookingSlug = bookingSlugFromPathname(pathname);

  useEffect(() => {
    const name = [userFirstName, userLastName].filter(Boolean).join(' ').trim();

    syncNoddiSupportWidget({
      enabled,
      // Host default: hide knowledge search / AI until we opt in (AND with admin config in hub).
      enableKnowledgeSearch: false,
      enableAiChat: false,
      boot: {
        brand: currentBrand,
        locale: toWidgetLocale(currentLanguage),
        environment: isProduction ? 'production' : 'staging',
        sourceApp: 'customer',
        appVersion: typeof __SOURCE_VERSION__ === 'string' ? __SOURCE_VERSION__ : undefined,
        pathname
      },
      identity:
        enabled && isLoggedIn && userId != null
          ? {
              userId,
              name: name || undefined,
              email: userEmail || undefined,
              phone: userPhone || undefined
            }
          : null,
      context: {
        pathname,
        serviceDepartmentId,
        licensePlate,
        car,
        bookingSlug
      }
    });
  }, [
    enabled,
    currentBrand,
    currentLanguage,
    isLoggedIn,
    userId,
    userEmail,
    userPhone,
    userFirstName,
    userLastName,
    pathname,
    licensePlate,
    car,
    serviceDepartmentId,
    bookingSlug
  ]);

  return null;
};
